import {
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  Input,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ShippingForm from "./components/Form/ShippingForm";
import FormStepper from "../../components/FormStepper";
import LanguageSelector from "../../components/LanguageSelector";
import LogoImg from "../../assets/linkz-logo-small.png";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { theme } from "../../components/Theme";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import OrderSummary from "./components/Form/OrderSummary";
import { If } from "../../components/Condition";
import VerifyOTP from "./components/Form/VerifyOTP";
import FinalizeOrderSection from "./components/Form/FinalizeOrderSection";
import { CatalogType } from "../../types/catalog";
import { CartDataType } from "../../types/cart";
import { InventoryService } from "../../services/inventory.service";
import NotFound from "../NotFound/NotFound";
import useFormContainer from "./useFormContainer";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import { PaymentService, PaymentType } from "../../services/payment.service";
import { getActiveBusiness, useAuthStore } from "../../store/session";
import { CatalogInventoryItemType } from "../../types/item";
import { useFeeStore } from "./components/Form/store";
import { BusinessService } from "../../services/business.service";
import { IExternalBusiness } from "../../types/business";
import { OrderService } from "../../services/order.service";
import { OrderStatusEnum, PaymentFlowTypeEnum } from "../../types/order";
import {
  calculatePrice,
  getConversionRate,
  trackCart,
  updateCartQty,
} from "../../utils/cart";
import PaymentInfoModal from "../../components/payments/PaymentInfoModal";
import showErrorToast, { ErrorToastConfig } from "../../components/Error";
import toast from "react-hot-toast";
import { IAddressResponse } from "../../types/profile";
import PaymentSuccessModal from "./components/PaymentSuccessModal";
import PaymentOptions from "../../components/payments/PaymentOptions";
import { getCountryInfo } from "../../utils/country";
import { BNPLPaymentModal } from "../../components/payments/BNPLPaymentModal";
import { OfflinePaymentModal } from "../../components/payments/OfflinePaymentModal";
import { privateAxios } from "../../lib/api";

const PublicCartCheckout = () => {
  const { t } = useTranslation();

  const STEPS = [
    {
      step: 1,
      title: t("catalog.checkout.steps.1"),
    },
    {
      step: 2,
      title: t("catalog.checkout.steps.2"),
    },
    {
      step: 3,
      title: t("catalog.checkout.steps.3"),
    },
  ];

  const { catalogId } = useParams();

  const { session } = useAuthStore();

  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<CatalogType>();
  const [checkOutItems, setCheckOutItems] = useState<CartDataType>();
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState<{
    show: boolean;
    isCompleted: boolean;
  }>({ show: false, isCompleted: false });
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const [showPaymentOfflineModal, setShowPaymentOfflineModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [paymentTransactionDetails, setPaymentTransactionDetails] =
    useState<any>(null);
  const [_orderId, setOrderId] = useState<string>();
  const [items, setItems] = useState<CatalogInventoryItemType[]>([]);
  const [bnplPayment, setBNPLPayment] = useState(false);
  const [paymentAttempts, setPaymentAttempts] = useState(0);
  const [searchParam, setSearchParams] = useSearchParams();
  const [isCodeValid, setCodeValid] = useState<null | boolean>(null);

  const methods = useForm();

  const {
    handleSubmit,
    getValues,
    watch,
    register,
    formState: { isValid, errors },
    control,
    setError,
    clearErrors,
    setValue,
  } = methods;

  const formContainer = useFormContainer(methods, catalog);
  const {
    isLoading,
    setIsLoading,
    currentStep,
    isValidAddress,
    otpInput,
    handleValidateStep,
    handleSubmitAddress,
    handleSubmitOTP,
  } = formContainer;

  // zustand
  const {
    paymentFee,
    deliveryFee,
    taxFee,
    setPaymentFee,
    subtotal,
    subtotalMultiPayment,
    discount,
  } = useFeeStore();

  const callbackURL = encodeURIComponent(`/cart/checkout/${catalogId}/items`);

  /**
   * fetch catalog based on catalogId
   */
  useEffect(() => {
    if (catalogId) {
      setIsLoading(true);
      InventoryService.getOneCatalog({
        catalogId: catalogId,
      })
        .then((res) => {
          setCatalog(res);
          setCheckOutItems(
            JSON.parse(sessionStorage.getItem("checkOutItems") || "{}")
          );
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [catalogId]);

  const checkReferralCode = async () => {
    const code = getValues("referral_code") as string;
    if (code.trim() == "") return true;

    try {
      const { data } = await privateAxios.get(
        `/referrals/check-code?code=${code}`
      );
      clearErrors("referral_code");
      setCodeValid(true);
      localStorage.setItem("referral_code", code);
      localStorage.setItem("referral_owner", data.owner);
      return true;
    } catch (e) {
      console.log(e);
      localStorage.removeItem("referral_code");
      localStorage.removeItem("referral_owner");
      setCodeValid(false);
      setError("referral_code", { message: "Incorrect referral code" });
      return false;
    }
  };

  const onSubmit = async (data: any) => {
    console.log("SUBMITTED DATA", data);

    if (!paymentTransactionDetails) {
      const paymentMethod = getValues("paymentMethod");
      if (paymentMethod === "offline-payment") {
        setPaymentAttempts((prev) => prev + 1);
        setShowPaymentOfflineModal(true);
        return;
      } else if (paymentMethod === "linkz-bnpl") {
        setPaymentAttempts((prev) => prev + 1);
        setBNPLPayment(true);
        return;
      }
    }

    const selectedIds = items.map((i) => i.inventoryItem.id);
    handleCreateOrder(selectedIds, items);
  };

  useEffect(() => {
    setSearchParams();
  }, []);

  const getPaymentStatus = ({
    orderId,
    onSuccess,
  }: {
    orderId: string;
    onSuccess?: () => void;
  }) => {
    let paymentType = PaymentType.FINAL_PAYMENT;
    if (catalog?.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
      paymentType = "1" as any;
    }
    PaymentService.enquiryPaymentStatus(
      orderId,
      catalog?.business.id || "",
      `${paymentType}` as any
    ).then((response) => {
      setPaymentInfo({
        ...response.data.transactionInfo,
        updatedAt: response.data.updatedAt,
        isCompleted: response.data.isCompleted,
        status: response.data.status,
      });
      setPaymentTransactionDetails(response.data);
      onSuccess?.();
    });
  };

  const handlePaymentContinue = async (orderId: string, amount: number) => {
    if (!catalog) {
      return;
    }

    const payload = getValues();
    const method = payload.paymentMethod?.split(":")[0];
    const option = payload.paymentMethod?.split(":")[1];
    let paymentType = PaymentType.FINAL_PAYMENT;
    let orderStatus = OrderStatusEnum.CONFIRMED;

    if (catalog.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
      paymentType = "1" as any;
      orderStatus = OrderStatusEnum.PENDING_FINAL_PAYMENT;
    } else if (
      catalog.paymentFlowType === PaymentFlowTypeEnum.FULL_AFTER_DELIVERY
    ) {
      orderStatus = OrderStatusEnum.PROCESSING;
    }

    await OrderService.updateOrderStatus(orderId, orderStatus);

    if (catalog.paymentFlowType === PaymentFlowTypeEnum.FULL_AFTER_DELIVERY) {
      setIsLoading(false);
      sessionStorage.removeItem("checkoutOrderId");
      window.location.href = `/client/orders/${orderId}`;
      return;
    }

    if (method) {
      console.log("Payable Amount", amount, getValues("status"));
      await PaymentService.requestPayment(
        PaymentService.createPaymentRequestPayload({
          method,
          option: option ?? "",
          transactionType: "order",
          transactionId: orderId,
          amount: amount + paymentFee,
          currency: catalog.currency,
          redirectUrl: window.location.origin + `/client/orders/${orderId}`,
          paymentType: `${paymentType}` as any,
        }),
        catalog.business.id
      )
        .then((response: any) => {
          if (response) {
            if (getValues("paymentMethod") == "linkz-bnpl") {
              setShowPaymentSuccessModal({
                show: true,
                isCompleted: true,
              });
              return;
            }

            if (getValues("paymentMethod") !== "offline-payment")
              getPaymentStatus({
                orderId,
                onSuccess: () => {
                  setPaymentAttempts((prev) => prev + 1);
                  setShowPaymentInfoModal(true);
                },
              });
            else {
              sessionStorage.removeItem("checkoutOrderId");
              window.location.href = `/client/orders/${orderId}`;
            }
          }
        })
        .catch((err: any) => {
          console.log(err);
          toast(showErrorToast(err), ErrorToastConfig);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleCreateOrder = (
    selectedIds: string[],
    items: CatalogInventoryItemType[]
  ) => {
    setIsLoading(true);
    const orderExist = sessionStorage.getItem("checkoutOrderId");

    if (orderExist) {
      if (paymentTransactionDetails) {
        setIsLoading(false);
        setShowPaymentInfoModal(true);
        return;
      }

      let amount =
        parseFloat(subtotal.toString()) +
        parseFloat(deliveryFee.toString()) +
        parseFloat(taxFee.toString()) -
        parseFloat(discount.toString());
      if (catalog?.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
        amount = parseFloat(subtotalMultiPayment.toString());
      }
      handlePaymentContinue(orderExist, amount);
      return;
    }

    if (catalog && !orderExist) {
      BusinessService.getMyBusinessRoles(getActiveBusiness()?.id || "").then(
        (_res) => {
          const sellerBusiness: IExternalBusiness = catalog.business;

          if (sellerBusiness) {
            // const sellerAccount = sellerBusiness?.role?.[0]?.account?.id ?? "";
            const roles = sellerBusiness.role as any[];
            const sellerAccount: any =
              sellerBusiness.role.find((r: any) => r.isOwner) ?? roles[0];

            if (sellerAccount) {
              OrderService.initiateDraft({
                grandTotalAmount: 0,
                paymentTerm: "",
                sellerId: catalog?.business.id || "",
                buyerId: getActiveBusiness()?.id || "",
                invoiceNumber: "",
                createdById: session?.account.id || null,
                status: OrderStatusEnum.DRAFT,
                isFromCatalog: true,
                isFromCatalogPrivate: catalog.catalogType === "PRIVATE",
                isUnPaidCatalogueOrder: true,
              })
                .then((res) => {
                  if (res.data.id) {
                    const selectedItems = items.filter((i) =>
                      selectedIds.includes(i.inventoryItem.id)
                    );
                    const itemsToBeOrdered = selectedItems.map((i) => {
                      const cartItem = checkOutItems?.items.find(
                        (item) => item.cid === i.id
                      );
                      return {
                        productId: i.inventoryItem.id,
                        sku: i.inventoryItem.sku,
                        productName: i.inventoryItem.productName,
                        quantity: cartItem?.quantity ?? 1,
                        uom: i.inventoryItem[cartItem?.uom ?? "uom1"],
                        conversion: getConversionRate(
                          cartItem?.uom ?? "uom1",
                          i.inventoryItem
                        ),
                        price: calculatePrice(i, cartItem?.uom ?? "uom1"),
                        discount: 0,
                        totalPrice:
                          calculatePrice(i, cartItem?.uom ?? "uom1") *
                          (cartItem?.quantity ?? 1),
                        businessId: i.inventoryItem.business,
                        inventoryType: i.inventoryItem.inventoryType,
                        currency: catalog.currency ?? i.currency,
                        productDescription: i.inventoryItem.productDescription,
                        createdById: i.createdBy || "",
                      };
                    });

                    let orderUpdatePayload = {
                      currency:
                        items[0].currency ?? items[0].inventoryItem.currency,
                      orderItems: itemsToBeOrdered,
                      grandTotalAmount:
                        subtotal + taxFee + deliveryFee - discount,
                      paymentFlowType: catalog.paymentFlowType,
                      status: OrderStatusEnum.AWAITING_RESPONSE,
                      paymentMethod: getValues("paymentMethod"),
                      createdById: session?.account.id || "",
                      sellerIds: {
                        businessId: catalog?.business.id,
                        accountId: sellerAccount.account,
                      },
                      buyerIds: {
                        businessId: getActiveBusiness()?.id || "",
                        accountId: session?.account.id || "",
                      },
                      deliveryFee,
                      totalTax: taxFee,
                      additionalTaxLabel: catalog?.taxId?.taxLabel ?? undefined,
                      additionalTax: catalog?.taxId?.taxAmount,
                      additionalTaxType: catalog?.taxId?.taxType,
                      additionalTaxAmountType: catalog?.taxId?.taxAmountType,
                      additionalDiscount: discount,
                      additionalDiscountType:
                        catalog?.discountFeeConfig?.amountType ?? "PERCENTAGE",
                      orderShippingAddress: getValues(
                        "address"
                      ) as IAddressResponse,
                      remarks: getValues("remarks"),
                    };

                    if (
                      catalog.paymentFlowType ===
                        PaymentFlowTypeEnum.MULTIPLE_PAYMENTS &&
                      catalog.multiPaymentsData
                    ) {
                      const multiPaymentsData = {
                        paymentTimes: catalog.multiPaymentsData.paymentTimes,
                        paymentTypes: catalog.multiPaymentsData.paymentTypes,
                        paymentHistory:
                          catalog.multiPaymentsData.paymentHistory.map((e) => {
                            return {
                              time: e.time,
                              percentage: e.percentage,
                              status: "PENDING",
                              amount:
                                ((parseFloat(subtotal.toString()) +
                                  parseFloat(taxFee.toString()) +
                                  parseFloat(deliveryFee.toString()) -
                                  parseFloat(discount.toString())) *
                                  parseFloat(e.percentage.toString())) /
                                100,
                            };
                          }),
                      };
                      orderUpdatePayload = {
                        ...orderUpdatePayload,
                        multiPaymentsData,
                      } as any;
                    }

                    if (catalog.currency) {
                      orderUpdatePayload.currency = catalog.currency;
                    }

                    OrderService.update(res.data.id, orderUpdatePayload)
                      .then((res) => {
                        if (checkOutItems) {
                          trackCart({
                            cartItems: checkOutItems,
                            catalog,
                            timing: "post",
                            items,
                          });
                        }

                        items.forEach((i) => {
                          const checkOutItem = checkOutItems?.items.find(
                            (ci) => ci.cid === i.id
                          );
                          updateCartQty({
                            catalogId: catalog.id,
                            accountId: session?.account.id || "wildcart",
                            override: false,
                            quantityChange: -(checkOutItem?.quantity || 0),
                            inventoryItem: i,
                            direct: session?.account.id ? false : true,
                            activeUOM: checkOutItem?.uom || "uom1",
                            storageType: session?.account.id
                              ? "local"
                              : "session",
                          });
                        });

                        if (res.id) {
                          setOrderId(res.id);
                          sessionStorage.setItem("checkoutOrderId", res.id);

                          let amount =
                            parseFloat(subtotal.toString()) +
                            parseFloat(deliveryFee.toString()) +
                            parseFloat(taxFee.toString()) -
                            parseFloat(discount.toString());

                          if (
                            catalog.paymentFlowType ===
                            PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                          ) {
                            amount = parseFloat(
                              subtotalMultiPayment.toString()
                            );
                          }

                          handlePaymentContinue(res.id, amount);
                        }
                      })
                      .catch((error) => {
                        throw new Error(error);
                      });
                  }
                })
                .catch((error) => console.error(error));
            }
          } else {
            setIsLoading(false);
            toast(t("Error: Seller Not Found", ErrorToastConfig));
          }
        }
      );
    }
  };

  if (!catalogId) {
    return <NotFound />;
  }

  return (
    <Sheet
      sx={{
        width: "100%",
        p: { xs: "16px", md: "32px" },
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      {/* guest header  */}
      <Stack
        direction={"row"}
        sx={{
          width: "100%",
          alignItems: "center",
          px: "4px",
        }}
      >
        {/* logo */}
        <Link to={"/"}>
          <AspectRatio
            ratio={1.09 / 0.5}
            variant="plain"
            sx={{
              display: { xs: "none", md: "flex" },
              width: "109.05px",
              height: "52px",
              mr: "auto",
            }}
          >
            <img src={LogoImg} alt="linkz-logo" />
          </AspectRatio>
        </Link>

        {/* navigation mobile */}
        <Stack
          direction={"row"}
          sx={{
            display: { xs: "flex", lg: "none" },
            gap: "0.5rem",
            mr: "auto",
            alignItems: "center",
          }}
        >
          <IconButton onClick={() => navigate(-1)}>
            <ArrowLeftIcon width={20} height={20} />
          </IconButton>

          <Typography
            level={"text-md-bold"}
            textColor={theme.palette.common.black}
          >
            {t("breadcrumb.checkout")}
          </Typography>
        </Stack>

        <Box sx={{ display: { xs: "flex", md: "none" } }}>
          <LanguageSelector />
        </Box>

        {/* navigation lg */}
        <Stack
          direction={"row"}
          sx={{
            display: { xs: "none", lg: "flex" },
            gap: "0.5rem",
            ml: "auto",
          }}
        >
          <LanguageSelector />

          {!session && (
            <Link to={`/signin?callbackURL=${callbackURL}`} className="ml-3">
              <Button variant="primary-light" className="min-w-[3rem]">
                {t("landing.loginBtn")}
              </Button>
            </Link>
          )}
        </Stack>
      </Stack>

      <Divider
        sx={{
          width: "100%",
          display: { xs: "none", md: "block" },
          backgroundColor: theme.palette.others.disabled,
        }}
      />

      {/* form */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={"column"} gap={"32px"}>
            <FormStepper
              currentStep={currentStep}
              onClick={() => {}}
              steps={STEPS}
              validation={handleValidateStep}
            />

            {catalog && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{
                  alignItems: "flex-start",
                  gap: "32px",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                  }}
                >
                  {/* signup form flow */}
                  {/* // TODO by Agung */}
                  <If condition={currentStep === 1}>
                    <ShippingForm formContainer={formContainer} />
                  </If>

                  {/* verify OTP form */}
                  <If condition={currentStep === 2}>
                    <VerifyOTP formContainer={formContainer} />
                  </If>

                  {/* finalize order */}
                  <If condition={currentStep === 3}>
                    <FinalizeOrderSection catalog={catalog} />
                  </If>
                </Box>

                {checkOutItems && (
                  <Stack flex={1} direction={"column"} gap={2}>
                    {searchParam.get("newUser") == "true" &&
                      currentStep === 1 && (
                        <Box
                          sx={{
                            flex: 1,
                            p: "16px",
                            border: `1px solid ${theme.palette.neutral[300]}`,
                            borderRadius: "8px",
                          }}
                        >
                          <Typography level="body-md" fontWeight="bold" mb={1}>
                            Do You Have a Referral Code?
                          </Typography>
                          <Stack direction={"row"} gap={1}>
                            <Input
                              className="w-full"
                              {...register("referral_code")}
                              placeholder={t(
                                "catalog.checkout.orderSummary.referralCodePlaceholder"
                              )}
                            />

                            <Button type="button" onClick={checkReferralCode}>
                              {t("catalog.checkout.orderSummary.applyBtn")}
                            </Button>
                          </Stack>
                          <Typography
                            level="body-xs"
                            color={isCodeValid ? "success" : "danger"}
                            mt={1}
                          >
                            {isCodeValid
                              ? "Referral code applied"
                              : (errors?.referral_code?.message as string)}
                          </Typography>
                          <Typography level="body-xs" color="success" mt={1}>
                            {}
                          </Typography>
                        </Box>
                      )}

                    <Box
                      sx={{
                        flex: 1,
                        p: "16px",
                        border: `1px solid ${theme.palette.neutral[300]}`,
                        borderRadius: "8px",
                      }}
                    >
                      <OrderSummary
                        catalog={catalog}
                        checkOutItems={checkOutItems}
                        items={items}
                        setItems={setItems}
                      />
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}

            {/* buttons mainly for mobile */}
            <Stack
              direction={"row"}
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <If
                condition={
                  currentStep === 1 && !!localStorage.getItem("method")
                }
              >
                <Button
                  fullWidth
                  className="h-10"
                  onClick={handleSubmitAddress}
                  disabled={!isValidAddress}
                >
                  {t("commons.nextBtn")}
                </Button>
              </If>

              <If condition={currentStep === 2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  sx={{
                    mt: "24px",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: "8px",
                    px: "16px",
                    gap: "24px",
                    display: { xs: "flex", md: "none" },
                  }}
                >
                  {/* terms and conditions */}
                  <FormControl size="md">
                    <Stack
                      direction={"row"}
                      sx={{
                        alignItems: "center",
                        width: "100%",
                        gap: "12px",
                      }}
                    >
                      <Checkbox
                        {...register("agreed")}
                        required
                        sx={{
                          alignSelf: "start",
                        }}
                      />
                      <Typography fontSize={14} fontWeight={400}>
                        {t("landing.signup.acceptTerms")}
                        <span
                          // onClick={() => setShowTermsModal(true)}
                          style={{
                            margin: "0 3px",
                            textDecoration: "none",
                          }}
                        >
                          <a
                            href={`${import.meta.env.VITE_TC_URL ?? "#"}`}
                            target="_blank"
                          >
                            <span
                              className={
                                "text-[#499873] cursor-pointer font-bold"
                              }
                            >
                              {t("landing.signup.terms")}
                            </span>
                          </a>
                        </span>
                        &
                        <span
                          // onClick={() => setPrivacyPolicy(true)}
                          style={{
                            marginLeft: "3px",
                            textDecoration: "none",
                          }}
                        >
                          <a
                            href={`${import.meta.env.VITE_PP_URL ?? "#"}`}
                            target="_blank"
                          >
                            <span
                              className={
                                "text-[#499873] cursor-pointer font-bold"
                              }
                            >
                              {t("landing.signup.privacy")}
                            </span>
                          </a>
                        </span>
                        .
                      </Typography>
                    </Stack>
                  </FormControl>

                  {/* proceed btn */}
                  <Button
                    id="public-checkout-verify-btn"
                    fullWidth
                    sx={{
                      maxWidth: { xs: "100%", lg: "100px" },
                      ml: "auto",
                    }}
                    disabled={!watch("agreed") || otpInput.length < 5}
                    onClick={() => handleSubmitOTP()}
                  >
                    {t("commons.proceedBtn")}
                  </Button>
                </Stack>
              </If>

              <If condition={currentStep === 3}>
                <Stack
                  direction={"column"}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    gap: "24px",
                  }}
                >
                  {/* payment section */}
                  <Stack
                    direction={"column"}
                    sx={{
                      gap: "16px",
                      p: "16px",
                      border: `1px solid ${theme.palette.neutral[300]}`,
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      level="title-md"
                      textColor={theme.palette.primary[500]}
                    >
                      {t("catalog.checkout.steps.3.2")}
                    </Typography>

                    <Typography
                      level="text-sm-medium"
                      textColor={theme.palette.neutral[600]}
                    >
                      {t("catalog.checkout.steps.3.2.subtitle")}
                    </Typography>

                    {/* payment options section */}
                    {catalog && (
                      <PaymentOptions
                        control={control}
                        setValue={setValue}
                        onlinePaymentOnly={catalog.catalogType === "PUBLIC"}
                        paymentAmount={
                          catalog.paymentFlowType ===
                          PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                            ? parseFloat(subtotalMultiPayment.toString())
                            : parseFloat(subtotal.toString()) +
                              parseFloat(deliveryFee.toString()) +
                              parseFloat(taxFee.toString()) -
                              parseFloat(discount.toString())
                        }
                        paymentCurrency={catalog?.currency}
                        handleChangePaymentFee={setPaymentFee}
                        applicableAdminFee={
                          catalog?.business.applicableAdminFee
                        }
                        sellerId={catalog.business.id}
                        sellerCurrency={
                          getCountryInfo(
                            catalog?.business.companyCountry ?? "ID",
                            "LOCALE"
                          )?.currency
                        }
                      />
                    )}
                  </Stack>

                  <Button
                    id="public-checkout-paynow-btn"
                    fullWidth
                    sx={{
                      maxWidth: { xs: "100%", lg: "100px" },
                      ml: "auto",
                    }}
                    disabled={
                      !isValid ||
                      !handleValidateStep(3) ||
                      !getValues("address")
                    }
                    type={"submit"}
                  >
                    {t("catalog.checkout.paynowBtn")}
                  </Button>
                </Stack>
              </If>
            </Stack>
          </Stack>
        </form>
      </FormProvider>

      {/* modals */}
      <LoadingModal isLoading={isLoading} />

      <BNPLPaymentModal
        paymentAttempts={paymentAttempts}
        open={bnplPayment}
        setOpenModal={(open: boolean) => {
          setBNPLPayment(open);
        }}
        onFinish={() => {
          const selectedIds = items.map((i) => i.inventoryItem.id);
          handleCreateOrder(selectedIds, items);
        }}
        onChangePaymentMethod={() => {
          setPaymentInfo(null);
          setPaymentTransactionDetails(null);
        }}
      />

      {catalog && paymentTransactionDetails && paymentInfo && (
        <PaymentInfoModal
          open={showPaymentInfoModal}
          paymentAttempts={paymentAttempts}
          setOpenModal={setShowPaymentInfoModal}
          paymentInfo={paymentInfo}
          status={paymentTransactionDetails?.status}
          onFinish={() => {
            setShowPaymentSuccessModal({
              show: true,
              isCompleted: false,
            });
          }}
          onChangePaymentMethod={() => {
            setPaymentInfo(null);
            setPaymentTransactionDetails(null);
          }}
          currency={catalog.currency}
        />
      )}

      <PaymentSuccessModal
        show={showPaymentSuccessModal.show}
        isCompleted={showPaymentSuccessModal.isCompleted}
        onClose={() => {
          sessionStorage.removeItem("checkoutOrderId");
          window.location.href = `/client/suppliers/${catalogId}/items`;
        }}
        onConfirm={() => {
          const orderExist = sessionStorage.getItem("checkoutOrderId");
          sessionStorage.removeItem("checkoutOrderId");
          window.location.href = `/client/orders/${orderExist}`;
        }}
      />

      <OfflinePaymentModal
        open={showPaymentOfflineModal}
        paymentAttempts={paymentAttempts}
        setOpenModal={(open: boolean) => {
          setShowPaymentOfflineModal(open);
        }}
        onChangePaymentMethod={() => {
          setPaymentInfo(null);
          setPaymentTransactionDetails(null);
        }}
        onFinish={() => {
          const selectedIds = items.map((i) => i.inventoryItem.id);
          handleCreateOrder(selectedIds, items);
        }}
      />
    </Sheet>
  );
};

export default PublicCartCheckout;
